import { createSlice } from "@reduxjs/toolkit";
import { nanoid } from "nanoid";
import syncedStorage from "../services/syncedStorage";
import { liveRows, stampRow, tombstoneFor } from "../utils/syncMeta";
import { CLIENTS_KEY, CLIENT_FORM_KEY } from "../constants/localKeys";
import subscriptionService from "../services/subscriptionService";
import userService from "../services/userService";
import { validateGSTIN } from "../utils/gstValidators";

const initialState = {
  openClientSelector: false,
  selectedClient: null,
  data: [],
  newForm: {
    id: nanoid(),
    image: "",
    name: "",
    email: "",
    billingAddress: "",
    mobileNo: "",
    GST: "",

  },
  editedID: null,
  deletedID: null,
};

export const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    addNewClient: (state, action) => {
      // Check subscription limits before adding client (async check will be handled by component)
      const user = userService.getCurrentUser();
      if (!user) {
        console.log('CLIENT CREATION BLOCKED: No user found');
        return;
      }

      // Validate client GSTIN if provided
      const clientGSTIN = action.payload.GST;
      if (clientGSTIN && clientGSTIN.trim()) {
        const gstValidation = validateGSTIN(clientGSTIN);
        if (!gstValidation.valid) {
          console.error('Invalid client GSTIN:', gstValidation.error);
          // Still allow saving but log the error
        }
      }
      
      const newDatas = [...state.data, stampRow(action.payload)];
      state.data = newDatas;
      syncedStorage.setItem(CLIENTS_KEY, newDatas);

      // Increment usage count in Supabase
      console.log(`📝 Attempting to increment clients count for ID: ${action.payload.id}`);
      userService.incrementResourceUsage('clients', action.payload.id)
        .then((result) => {
          console.log('✅ Client usage count incremented in Supabase:', result);
        })
        .catch(error => {
          console.error('❌ Failed to increment client usage count:', error);
        });

      const reNewForm = {
        id: nanoid(),
        image: "",
        name: "",
        email: "",
        billingAddress: "",
        mobileNo: "",
        GST: "",
      };

      state.newForm = { ...reNewForm };
      syncedStorage.setItem(CLIENT_FORM_KEY, reNewForm);
    },

    updateNewClientForm: (state, action) => {
      state.newForm = { ...action.payload };
      syncedStorage.setItem(CLIENT_FORM_KEY, { ...state.newForm });
    },

    updateNewClientFormField: (state, action) => {
      state.newForm[action.payload.key] = action.payload.value;
      syncedStorage.setItem(CLIENT_FORM_KEY, { ...state.newForm });
    },

    setAllClients: (state, action) => {
      let clientData = action.payload || [];
      
      // Apply subscription limits to existing data
      const user = userService.getCurrentUser();
      if (user) {
        const plans = subscriptionService.getSubscriptionPlans();
        const effectiveTier = subscriptionService.getEffectiveSubscriptionTier();
        const currentPlan = plans[effectiveTier] || plans.free;
        const clientLimit = currentPlan.limitations.clients;
        
        // If user has a limit and data exceeds it, truncate the data
        if (clientLimit !== -1 && clientData.length > clientLimit) {
          console.log(`CLIENT LIMIT ENFORCEMENT: Truncating ${clientData.length} clients to ${clientLimit} for ${currentPlan.name} plan`);
          clientData = clientData.slice(0, clientLimit);
        }
      }
      
      state.data = [...clientData];
    },

    setDeleteId: (state, action) => {
      state.deletedID = action.payload;
    },

    setEditedId: (state, action) => {
      state.editedID = action.payload;
    },

    onConfirmDeletedClient: (state, action) => {
      const deletedClient = state.data.find(client => client.id === state.deletedID);
      // Soft delete: replace the live row with a tombstone so the deletion
      // survives a per-row merge from another device. Selectors filter
      // tombstones — UI never sees them.
      const newDatas = state.data.map((client) =>
        client.id === state.deletedID ? tombstoneFor(client.id) : client
      );
      state.data = newDatas;
      state.deletedID = null;
      syncedStorage.setItem(CLIENTS_KEY, newDatas);

      // Decrement usage count in Supabase
      if (deletedClient) {
        userService.decrementResourceUsage('clients', deletedClient.id)
          .then(() => {
            console.log('Client usage count decremented in Supabase');
          })
          .catch(error => {
            console.error('Failed to decrement client usage count:', error);
          });
      }
    },

    onConfirmEditClient: (state, action) => {
      // Validate client GSTIN if provided
      const clientGSTIN = action.payload.GST;
      if (clientGSTIN && clientGSTIN.trim()) {
        const gstValidation = validateGSTIN(clientGSTIN);
        if (!gstValidation.valid) {
          console.error('Invalid client GSTIN:', gstValidation.error);
          // Still allow saving but log the error
        }
      }

      const isFindIndex = state.data.findIndex(
        (client) => client.id === state.editedID
      );
      if (isFindIndex !== -1) {
        state.data[isFindIndex] = stampRow({ ...action.payload });
      }
      state.editedID = null;
      syncedStorage.setItem(CLIENTS_KEY, [...state.data]);
    },

    setOpenClientSelector: (state, action) => {
      state.openClientSelector = action.payload;
      if (!action.payload) {
        state.selectedClient = null;
      }
    },

    setClientSelector: (state, action) => {
      const isFindIndex = state.data.findIndex(
        (client) => client.id === action.payload
      );
      if (isFindIndex !== -1) {
        state.selectedClient = state.data[isFindIndex];
      }
    },
  },
});

export const {
  addNewClient,
  updateNewClientForm,
  updateNewClientFormField,
  setAllClients,
  setDeleteId,
  setEditedId,
  onConfirmDeletedClient,
  onConfirmEditClient,
  setOpenClientSelector,
  setClientSelector,
} = clientsSlice.actions;

export const getAllClientsSelector = (state) => liveRows(state.clients.data);

export const getClientNewForm = (state) => state.clients.newForm;

export const getDeletedClientForm = (state) => state.clients.deletedID;

export const getEditedIdForm = (state) => state.clients.editedID;

export const getIsOpenClientSelector = (state) =>
  state.clients.openClientSelector;

export const getSelectedClient = (state) => state.clients.selectedClient;

export default clientsSlice.reducer;
